/**
 * Represents the type of a test or test-related entity using bitwise flags.
 */
export class TestType {
  /**
   * The numeric value representing the combined bitwise flags.
   */
  value: number;

  /**
   * Initializes a new TestType.
   * @param type - The numeric bitmask value.
   */
  constructor(type: number) {
    if (typeof type !== 'number') {
      throw new Error(`${TestType.name} must be a number. Instead saw ${type}`);
    }

    if (type < MIN_FLAG || type > MAX_FLAG) {
      throw new Error(`${TestType.name} value out of range. Instead saw ${type}`);
    }

    this.value = type;
  }

  /**
   * Checks if the specified bitmask is included in the current type value.
   * @param testFlag - The bitmask to check for.
   * @returns True if the flag is set.
   */
  has(testFlag: number): boolean {
    return (this.value & testFlag) === testFlag;
  }

  /**
   * Checks if any of the specified bitmasks are included in the current type value.
   * @param testFlags - The bitmasks to check for.
   * @returns True if at least one flag is set.
   */
  hasEither(...testFlags: number[]): boolean {
    for (let i = 0; i < testFlags.length; i++) {
      if (this.has(testFlags[i])) return true;
    }
    return false;
  }

  /**
   * Checks if the current type value is exactly equal to the specified bitmask.
   * @param testFlag - The bitmask to compare against.
   * @returns True if the values are equal.
   */
  is(testFlag: number): boolean {
    return this.value === testFlag;
  }

  /**
   * Checks if the current type value is exactly equal to any of the specified bitmasks.
   * @param testFlags - The bitmasks to compare against.
   * @returns True if the value matches any of the flags.
   */
  isEither(...testFlags: number[]): boolean {
    for (let i = 0; i < testFlags.length; i++) {
      if (this.is(testFlags[i])) return true;
    }
    return false;
  }

  /**
   * Returns a string representation of the set flags (for debugging).
   */
  toString(): string {
    const typeValue = this.value;
    return Object.keys(TestType)
      .filter(key => (TestType as any)[key] === typeValue)
      .join('|');
  }

  // Base Flags
  /** Indicates the entity has an 'only' property or decorator. */
  static HAS_ONLY = 1;
  /** Indicates the entity has a 'cases' property or decorator. */
  static HAS_CASES = 2;
  /** Indicates the entity has a custom 'title' property or decorator. */
  static HAS_TITLE = 4;
  /** Indicates the entity is a default export. */
  static EXPORT_DEFAULT = 8;

  // Composite Types
  /** Indicates the entity is a module. */
  static MODULE = 16;
  static MODULE_WITH_TITLE = TestType.MODULE | TestType.HAS_TITLE;

  /** Indicates the entity is a plain object. */
  static OBJECT = 32;
  static OBJECT_WITH_ONLY = TestType.OBJECT | TestType.HAS_ONLY;

  /** Indicates the entity is a function. */
  static FUNCTION = 64;
  static FUNCTION_WITH_CASES = TestType.FUNCTION | TestType.HAS_CASES;
  static FUNCTION_WITH_ONLY = TestType.FUNCTION | TestType.HAS_ONLY;
  static FUNCTION_WITH_CASES_ONLY = TestType.FUNCTION_WITH_CASES | TestType.HAS_ONLY;
  static FUNCTION_WITH_CASES_TITLE = TestType.FUNCTION_WITH_CASES | TestType.HAS_TITLE;
  static FUNCTION_WITH_TITLE = TestType.FUNCTION | TestType.HAS_TITLE;
  static FUNCTION_WITH_TITLE_ONLY = TestType.FUNCTION_WITH_TITLE | TestType.HAS_ONLY;

  /** Indicates the entity is a built-in test runner function (e.g., 'beforeEach'). */
  static BUILTIN = 128;
  static BUILTIN_FUNCTION = TestType.BUILTIN | TestType.FUNCTION;

  /** Indicates the entity is a class definition. */
  static CLASS_DEF = 256;
  static CLASS_DEF_EXPORT_DEFAULT = TestType.CLASS_DEF | TestType.EXPORT_DEFAULT;
  static CLASS_DEF_WITH_ONLY = TestType.CLASS_DEF | TestType.HAS_ONLY;
  static CLASS_DEF_WITH_TITLE = TestType.CLASS_DEF | TestType.HAS_TITLE;

  /** Indicates the entity is a class instance. */
  static CLASS_INST = 512;
  static CLASS_INST_FUNCTION = TestType.CLASS_INST | TestType.FUNCTION;
  static CLASS_INST_FUNCTION_WITH_CASES = TestType.CLASS_INST | TestType.FUNCTION_WITH_CASES;
  static CLASS_INST_FUNCTION_WITH_ONLY = TestType.CLASS_INST | TestType.FUNCTION_WITH_ONLY;
  static CLASS_INST_FUNCTION_WITH_ONLY_CASES = TestType.CLASS_INST | TestType.FUNCTION_WITH_CASES_ONLY;
  static CLASS_INST_FUNCTION_WITH_TITLE = TestType.CLASS_INST | TestType.FUNCTION_WITH_TITLE;
  static CLASS_INST_BUILTIN_FUNCTION = TestType.CLASS_INST | TestType.BUILTIN_FUNCTION;
}

const MIN_FLAG = TestType.HAS_ONLY;
const MAX_FLAG = TestType.CLASS_INST_BUILTIN_FUNCTION;
